import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import axiosInstance from '../services/AxiosInstance';

/**
 * Branch / department / sub-department / user multi-select for Credentials Vault,
 * aligned with document upload access (documentList.js).
 */
export function useCredentialVaultAccessSelections() {
  const [metadataLoaded, setMetadataLoaded] = useState(false);
  const [branches, setBranches] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [subDepartments, setSubDepartments] = useState([]);
  const [filteredDepartments, setFilteredDepartments] = useState([]);
  const [filteredSubDepartments, setFilteredSubDepartments] = useState([]);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);

  const [selectedBranches, setSelectedBranches] = useState([]);
  const [selectedDepartments, setSelectedDepartments] = useState([]);
  const [selectedSubDepartments, setSelectedSubDepartments] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [userAccessById, setUserAccessById] = useState({});
  const [branchAccessById, setBranchAccessById] = useState({});
  const [departmentAccessById, setDepartmentAccessById] = useState({});
  const [subDepartmentAccessById, setSubDepartmentAccessById] = useState({});

  const selectedUsersRef = useRef(selectedUsers);
  selectedUsersRef.current = selectedUsers;

  const corpUserMatchesId = (corporateUserId, user) => {
    const uid = typeof corporateUserId === 'number' ? corporateUserId : parseInt(corporateUserId, 10);
    if (!Number.isFinite(uid)) return false;
    const a = user?.id ?? user?.CorporateUserId;
    return a === uid || Number(a) === uid;
  };

  const userAclLevel = useCallback((raw) => {
    const lv = String(raw || '').toLowerCase().replace(/-/g, '_');
    return ['full', 'edit', 'view'].includes(lv) ? lv : 'view';
  }, []);

  const scopeAclLevel = useCallback((raw) => {
    if (raw == null || raw === '') return 'view';
    const lv = String(raw).toLowerCase().replace(/-/g, '_');
    if (lv === 'no_access' || lv === 'none') return 'no_access';
    return ['full', 'edit', 'view'].includes(lv) ? lv : 'view';
  }, []);

  const pickAccessForIds = useCallback(
    (ids, prev) => {
      const next = {};
      for (const raw of ids) {
        const id = typeof raw === 'number' ? raw : parseInt(raw, 10);
        if (!Number.isFinite(id)) continue;
        next[id] = scopeAclLevel(prev[id] ?? prev[String(id)]);
      }
      return next;
    },
    [scopeAclLevel]
  );

  const fetchUsersByFilters = async (branchIds, departmentIds, subDepartmentIds) => {
    try {
      const userData = JSON.parse(localStorage.getItem('user'));
      const corporateId = userData?.corporateId || userData?.id;
      if (!corporateId) return [];

      const params = new URLSearchParams();
      params.append('corporateId', parseInt(corporateId, 10));
      if (branchIds?.length > 0) params.append('branch', branchIds.join(','));
      if (departmentIds?.length > 0) params.append('department', departmentIds.join(','));
      if (subDepartmentIds?.length > 0) params.append('sub_department', subDepartmentIds.join(','));

      const response = await axiosInstance.get(`/list/corporateuserslist?${params.toString()}`);
      const users = response.data.users || [];
      return users.filter((user) => user.id !== userData.id);
    } catch (error) {
      console.error('Error fetching users by filters:', error);
      return [];
    }
  };

  const filterUsersBySelections = useCallback(async (branchIds, departmentIds, subDepartmentIds) => {
    if (branchIds.length > 0 || departmentIds.length > 0 || subDepartmentIds.length > 0) {
      const users = await fetchUsersByFilters(branchIds, departmentIds, subDepartmentIds);
      setFilteredUsers(users);
      const prev = selectedUsersRef.current;
      if (prev.length > 0) {
        const validUserIds = prev.filter(
          (userId) =>
            users.some((user) => corpUserMatchesId(userId, user)) ||
            availableUsers.some((user) => corpUserMatchesId(userId, user))
        );
        if (validUserIds.length !== prev.length) {
          setSelectedUsers(validUserIds);
          setUserAccessById((uPrev) => {
            const next = {};
            for (const id of validUserIds) {
              const idNum = typeof id === 'number' ? id : parseInt(id, 10);
              if (!Number.isFinite(idNum)) continue;
              next[idNum] = uPrev[idNum] || uPrev[String(idNum)] || 'view';
            }
            return next;
          });
        }
      }
    } else {
      setFilteredUsers(availableUsers);
    }
  }, [availableUsers]);

  const fetchBranches = useCallback(async () => {
    const userData = JSON.parse(localStorage.getItem('user'));
    const corporateId = userData?.corporateId || userData?.id;
    const res = await axiosInstance.get(`/settings/branches?corporate_id=${corporateId}`);
    setBranches(res.data.data || res.data || []);
  }, []);

  const fetchDepartments = useCallback(async () => {
    const userData = JSON.parse(localStorage.getItem('user'));
    const corporateId = userData?.corporateId || userData?.id;
    const res = await axiosInstance.get(`/settings/departments?corporate_id=${corporateId}`);
    const deptList = res.data.data || res.data || [];
    setDepartments(deptList);
    setFilteredDepartments(deptList);
  }, []);

  const fetchSubDepartments = useCallback(async () => {
    const userData = JSON.parse(localStorage.getItem('user'));
    const corporateId = userData?.corporateId || userData?.id;
    let res;
    try {
      res = await axiosInstance.get(`/settings/subDepartments?corporate_id=${corporateId}`);
    } catch (e1) {
      res = await axiosInstance.get(`/settings/subdepartments?corporate_id=${corporateId}`);
    }
    const subDeptList = res.data.data || res.data || [];
    setSubDepartments(subDeptList);
    setFilteredSubDepartments(subDeptList);
  }, []);

  const fetchUsers = useCallback(async () => {
    const userData = JSON.parse(localStorage.getItem('user'));
    const corporateId = userData?.corporateId || userData?.id;
    if (!corporateId) return;

    const params = new URLSearchParams();
    params.append('corporateId', parseInt(corporateId, 10));
    const response = await axiosInstance.get(`/list/corporateuserslist?${params.toString()}`);
    const users = response.data.users || [];
    const list = users.filter((user) => user.id !== userData.id);
    setAvailableUsers(list);
    setFilteredUsers(list);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await Promise.all([
          fetchBranches(),
          fetchDepartments(),
          fetchSubDepartments(),
          fetchUsers(),
        ]);
        if (!cancelled) setMetadataLoaded(true);
      } catch (e) {
        console.error('Credentials Vault access metadata load failed:', e);
        if (!cancelled) setMetadataLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [fetchBranches, fetchDepartments, fetchSubDepartments, fetchUsers]);

  const handleBranchChange = async (branchIds) => {
    setSelectedBranches(branchIds);
    setBranchAccessById((prev) => pickAccessForIds(branchIds, prev));

    if (branchIds.length > 0) {
      const filtered = departments.filter((dept) => branchIds.includes(dept.branch_id));
      setFilteredDepartments(filtered);

      const validDepartmentIds = selectedDepartments.filter((deptId) =>
        filtered.some((d) => d.id === deptId)
      );
      setSelectedDepartments(validDepartmentIds);
      setDepartmentAccessById((prev) => pickAccessForIds(validDepartmentIds, prev));

      let subs = [];
      if (validDepartmentIds.length > 0) {
        subs = subDepartments.filter((sd) => {
          const sdDeptId = sd.department_id || sd.DepartmentId || sd.departmentId;
          return validDepartmentIds.includes(sdDeptId);
        });
      } else {
        subs = subDepartments.filter((sd) => {
          const sdDeptId = sd.department_id || sd.DepartmentId || sd.departmentId;
          const dept = departments.find((d) => d.id === sdDeptId);
          return dept && branchIds.includes(dept.branch_id);
        });
      }
      setFilteredSubDepartments(subs);

      const validSubDepartmentIds = selectedSubDepartments.filter((subDeptId) =>
        subs.some((sd) => {
          const sdId = sd.id || sd.sub_department_id || sd.subDepartmentId;
          return sdId === subDeptId;
        })
      );
      setSelectedSubDepartments(validSubDepartmentIds);
      setSubDepartmentAccessById((prev) => pickAccessForIds(validSubDepartmentIds, prev));

      await filterUsersBySelections(branchIds, validDepartmentIds, validSubDepartmentIds);
    } else {
      setFilteredDepartments(departments);

      if (selectedDepartments.length > 0) {
        const subs = subDepartments.filter((sd) => {
          const sdDeptId = sd.department_id || sd.DepartmentId || sd.departmentId;
          return selectedDepartments.includes(sdDeptId);
        });
        setFilteredSubDepartments(subs);

        const validSubDepartmentIds = selectedSubDepartments.filter((subDeptId) =>
          subs.some((sd) => {
            const sdId = sd.id || sd.sub_department_id || sd.subDepartmentId;
            return sdId === subDeptId;
          })
        );
        setSelectedSubDepartments(validSubDepartmentIds);
        setSubDepartmentAccessById((prev) => pickAccessForIds(validSubDepartmentIds, prev));

        await filterUsersBySelections([], selectedDepartments, validSubDepartmentIds);
      } else {
        setFilteredSubDepartments(subDepartments);
        setFilteredUsers(availableUsers);
      }
    }
  };

  const handleDepartmentChange = async (departmentIds) => {
    setSelectedDepartments(departmentIds);
    setDepartmentAccessById((prev) => pickAccessForIds(departmentIds, prev));

    if (departmentIds.length > 0) {
      let subs = subDepartments.filter((sd) => {
        const sdDeptId = sd.department_id || sd.DepartmentId || sd.departmentId;
        return departmentIds.includes(sdDeptId);
      });

      if (selectedBranches.length > 0) {
        subs = subs.filter((sd) => {
          const sdDeptId = sd.department_id || sd.DepartmentId || sd.departmentId;
          const dept = departments.find((d) => d.id === sdDeptId);
          return dept && selectedBranches.includes(dept.branch_id);
        });
      }

      setFilteredSubDepartments(subs);

      const validSubDepartmentIds = selectedSubDepartments.filter((subDeptId) =>
        subs.some((sd) => {
          const sdId = sd.id || sd.sub_department_id || sd.subDepartmentId;
          return sdId === subDeptId;
        })
      );
      setSelectedSubDepartments(validSubDepartmentIds);
      setSubDepartmentAccessById((prev) => pickAccessForIds(validSubDepartmentIds, prev));

      await filterUsersBySelections(selectedBranches, departmentIds, validSubDepartmentIds);
    } else if (selectedBranches.length > 0) {
      const filtered = departments.filter((dept) => selectedBranches.includes(dept.branch_id));
      const filteredDeptIds = filtered.map((d) => d.id);
      const subs = subDepartments.filter((sd) => {
        const sdDeptId = sd.department_id || sd.DepartmentId || sd.departmentId;
        return filteredDeptIds.includes(sdDeptId);
      });
      setFilteredSubDepartments(subs);

      const validSubDepartmentIds = selectedSubDepartments.filter((subDeptId) =>
        subs.some((sd) => {
          const sdId = sd.id || sd.sub_department_id || sd.subDepartmentId;
          return sdId === subDeptId;
        })
      );
      setSelectedSubDepartments(validSubDepartmentIds);
      setSubDepartmentAccessById((prev) => pickAccessForIds(validSubDepartmentIds, prev));

      await filterUsersBySelections(selectedBranches, [], validSubDepartmentIds);
    } else {
      setFilteredSubDepartments(subDepartments);
      setFilteredUsers(availableUsers);
      setSelectedSubDepartments([]);
      setSubDepartmentAccessById({});
    }
  };

  const handleSubDepartmentChange = async (subDepartmentIds) => {
    setSelectedSubDepartments(subDepartmentIds);
    setSubDepartmentAccessById((prev) => pickAccessForIds(subDepartmentIds, prev));
    await filterUsersBySelections(selectedBranches, selectedDepartments, subDepartmentIds);
  };

  const handleUserChange = (userIds) => {
    setSelectedUsers(userIds);
    setUserAccessById((prev) => {
      const next = {};
      for (const id of userIds) {
        const idNum = typeof id === 'number' ? id : parseInt(id, 10);
        if (!Number.isFinite(idNum)) continue;
        next[idNum] = prev[idNum] || prev[String(idNum)] || 'view';
      }
      return next;
    });
  };

  const handleUserAccessLevelChange = useCallback(
    (corporateUserId, level) => {
      const uid = typeof corporateUserId === 'number' ? corporateUserId : parseInt(corporateUserId, 10);
      if (!Number.isFinite(uid)) return;
      setUserAccessById((prev) => ({ ...prev, [uid]: userAclLevel(level) }));
    },
    [userAclLevel]
  );

  const handleBranchAccessLevelChange = useCallback(
    (branchId, level) => {
      const id = typeof branchId === 'number' ? branchId : parseInt(branchId, 10);
      if (!Number.isFinite(id)) return;
      setBranchAccessById((prev) => ({ ...prev, [id]: scopeAclLevel(level) }));
    },
    [scopeAclLevel]
  );

  const handleDepartmentAccessLevelChange = useCallback(
    (departmentId, level) => {
      const id = typeof departmentId === 'number' ? departmentId : parseInt(departmentId, 10);
      if (!Number.isFinite(id)) return;
      setDepartmentAccessById((prev) => ({ ...prev, [id]: scopeAclLevel(level) }));
    },
    [scopeAclLevel]
  );

  const handleSubDepartmentAccessLevelChange = useCallback(
    (subDepartmentId, level) => {
      const id = typeof subDepartmentId === 'number' ? subDepartmentId : parseInt(subDepartmentId, 10);
      if (!Number.isFinite(id)) return;
      setSubDepartmentAccessById((prev) => ({ ...prev, [id]: scopeAclLevel(level) }));
    },
    [scopeAclLevel]
  );

  /**
   * Pre-fill selects when editing an existing Credential (after lists are loaded).
   */
  const hydrateFromCredential = useCallback(
    async (acc) => {
      if (!acc) return;
      const bi = (acc.branch_ids || []).map((x) => parseInt(x, 10)).filter((n) => Number.isFinite(n));
      const di = (acc.department_ids || []).map((x) => parseInt(x, 10)).filter((n) => Number.isFinite(n));
      const si = (acc.sub_department_ids || []).map((x) => parseInt(x, 10)).filter((n) => Number.isFinite(n));
      const ui = (acc.user_ids || []).map((x) => parseInt(x, 10)).filter((n) => Number.isFinite(n));

      setSelectedBranches(bi);

      const branchGrantMap = {};
      if (Array.isArray(acc.branch_grants)) {
        acc.branch_grants.forEach((g) => {
          const id = parseInt(g.branch_id, 10);
          if (!Number.isFinite(id)) return;
          branchGrantMap[id] = scopeAclLevel(g.access_level);
        });
      }
      const nextBranchAccess = {};
      bi.forEach((id) => {
        nextBranchAccess[id] = scopeAclLevel(branchGrantMap[id]);
      });
      setBranchAccessById(nextBranchAccess);

      if (bi.length > 0) {
        const fd = departments.filter((dept) => bi.includes(dept.branch_id));
        setFilteredDepartments(fd);
      } else {
        setFilteredDepartments(departments);
      }

      setSelectedDepartments(di);

      const deptGrantMap = {};
      if (Array.isArray(acc.department_grants)) {
        acc.department_grants.forEach((g) => {
          const id = parseInt(g.department_id, 10);
          if (!Number.isFinite(id)) return;
          deptGrantMap[id] = scopeAclLevel(g.access_level);
        });
      }
      const nextDeptAccess = {};
      di.forEach((id) => {
        nextDeptAccess[id] = scopeAclLevel(deptGrantMap[id]);
      });
      setDepartmentAccessById(nextDeptAccess);

      if (di.length > 0) {
        let subs = subDepartments.filter((sd) => {
          const sdDeptId = sd.department_id || sd.DepartmentId || sd.departmentId;
          return di.includes(sdDeptId);
        });
        if (bi.length > 0) {
          subs = subs.filter((sd) => {
            const sdDeptId = sd.department_id || sd.DepartmentId || sd.departmentId;
            const dept = departments.find((d) => d.id === sdDeptId);
            return dept && bi.includes(dept.branch_id);
          });
        }
        setFilteredSubDepartments(subs);
      } else if (bi.length > 0) {
        const fdept = departments.filter((d) => bi.includes(d.branch_id));
        const deptIds = fdept.map((d) => d.id);
        const subs = subDepartments.filter((sd) => {
          const sdDeptId = sd.department_id || sd.DepartmentId || sd.departmentId;
          return deptIds.includes(sdDeptId);
        });
        setFilteredSubDepartments(subs);
      } else {
        setFilteredSubDepartments(subDepartments);
      }

      setSelectedSubDepartments(si);

      const subGrantMap = {};
      if (Array.isArray(acc.sub_department_grants)) {
        acc.sub_department_grants.forEach((g) => {
          const id = parseInt(g.sub_department_id ?? g.subdepartment_id, 10);
          if (!Number.isFinite(id)) return;
          subGrantMap[id] = scopeAclLevel(g.access_level);
        });
      }
      const nextSubAccess = {};
      si.forEach((id) => {
        nextSubAccess[id] = scopeAclLevel(subGrantMap[id]);
      });
      setSubDepartmentAccessById(nextSubAccess);

      const grantsMap = {};
      if (Array.isArray(acc.user_grants)) {
        acc.user_grants.forEach((g) => {
          const cid = parseInt(g.corporate_user_id, 10);
          if (!Number.isFinite(cid)) return;
          grantsMap[cid] = userAclLevel(g.access_level);
        });
      }
      const accessForSelected = {};
      ui.forEach((id) => {
        accessForSelected[id] = userAclLevel(grantsMap[id]);
      });
      setUserAccessById(accessForSelected);

      setSelectedUsers(ui);
      await filterUsersBySelections(bi, di, si);
    },
    [departments, subDepartments, filterUsersBySelections, userAclLevel, scopeAclLevel]
  );

  /** Options for user multi-select + labels for permission rows (includes grantees outside current branch filter). */
  const usersForCredentialSelect = useMemo(() => {
    const map = new Map();
    filteredUsers.forEach((u) => {
      const id = u?.id ?? u?.CorporateUserId;
      if (id == null) return;
      const n = typeof id === 'number' ? id : parseInt(id, 10);
      if (!Number.isFinite(n)) return;
      map.set(n, u);
    });
    selectedUsers.forEach((sid) => {
      const id = typeof sid === 'number' ? sid : parseInt(sid, 10);
      if (!Number.isFinite(id) || map.has(id)) return;
      const found = availableUsers.find((u) => corpUserMatchesId(id, u));
      if (found) map.set(id, found);
      else map.set(id, { id, full_name: `User #${id}` });
    });
    return [...map.values()];
  }, [filteredUsers, selectedUsers, availableUsers]);

  const clearAccessSelections = useCallback(() => {
    setSelectedBranches([]);
    setSelectedDepartments([]);
    setSelectedSubDepartments([]);
    setSelectedUsers([]);
    setUserAccessById({});
    setBranchAccessById({});
    setDepartmentAccessById({});
    setSubDepartmentAccessById({});
    setFilteredDepartments(departments);
    setFilteredSubDepartments(subDepartments);
    setFilteredUsers(availableUsers);
  }, [departments, subDepartments, availableUsers]);

  return {
    metadataLoaded,
    branches,
    departments,
    subDepartments,
    filteredDepartments,
    filteredSubDepartments,
    availableUsers,
    filteredUsers,
    usersForCredentialSelect,
    selectedBranches,
    selectedDepartments,
    selectedSubDepartments,
    selectedUsers,
    userAccessById,
    branchAccessById,
    departmentAccessById,
    subDepartmentAccessById,
    handleBranchChange,
    handleDepartmentChange,
    handleSubDepartmentChange,
    handleUserChange,
    handleUserAccessLevelChange,
    handleBranchAccessLevelChange,
    handleDepartmentAccessLevelChange,
    handleSubDepartmentAccessLevelChange,
    hydrateFromCredential,
    clearAccessSelections,
  };
}
